import React, { useState } from 'react';
import { Button, InputNumber, Modal, Select, Space, Tooltip, Typography } from 'antd';
import type { ExpV2ActionBody, ExpV2Test } from '@/types/experimentsV2';
import {
  availableActions,
  rolloutSizeProblem,
  startBlockedBy,
  variantChoices,
  type ActionOption,
} from './actions';
import { bucketsToPercent, formatPercent, percentToBuckets } from './units';

interface Props {
  test: ExpV2Test;
  canEdit: boolean;
  /** Pre-registration fields still empty (Start stays disabled until filled). */
  preMissing: string[];
  onRun: (option: ActionOption, body: ExpV2ActionBody) => void | Promise<void>;
}

/** Spec "Safety phase": a test opens on a small slice first. */
const START_PERCENT = 1;
const WAITS = "This will wait for a second admin's approval.";

const CONSEQUENCE: Partial<Record<ActionOption['action'], string>> = {
  pause: 'Players get no params from this test until resumed.',
  resume: 'Players get their variant params again.',
  go_to_target: 'Opens the whole reserved share; the stable phase starts now.',
  end: 'Frees the range; players go back to defaults.',
};

const VARIANT_CONSEQUENCE: Partial<Record<ActionOption['action'], string>> = {
  pause_variant: "Its players get control's params until it is resumed.",
  resume_variant: 'Its players get its params again.',
  remove_variant: "Final: its players get control's params.",
  roll_out_winner: "The chosen variant's params go to every matching player (holdout excluded).",
};

export const ActionBar: React.FC<Props> = ({ test, canEdit, preMissing, onRun }) => {
  const [sizeFor, setSizeFor] = useState<ActionOption | null>(null);
  const [percent, setPercent] = useState<number | null>(START_PERCENT);
  const [variantFor, setVariantFor] = useState<ActionOption | null>(null);
  const [variant, setVariant] = useState<string | undefined>();

  const options = availableActions(test, { canEdit });
  if (options.length === 0) return null;
  const blocked = startBlockedBy(test, preMissing);

  const open = (o: ActionOption) => {
    if (o.needs === 'rolloutSize') {
      setPercent(o.action === 'start' ? START_PERCENT : bucketsToPercent(test.rolloutSize));
      setSizeFor(o);
    } else if (o.needs === 'variant') {
      setVariant(undefined);
      setVariantFor(o);
    } else {
      Modal.confirm({
        title: `${o.label} ${test.key}?`,
        content: [CONSEQUENCE[o.action], o.requested ? WAITS : null].filter(Boolean).join(' '),
        okButtonProps: { danger: o.danger },
        onOk: () => onRun(o, {}),
      });
    }
  };

  const problem = sizeFor ? rolloutSizeProblem(test, sizeFor.action, percent) : null;
  const growsRange =
    sizeFor?.action === 'raise' &&
    percent !== null &&
    (percentToBuckets(percent) ?? 0) > test.rangeSize;

  return (
    <Space wrap data-section="actions">
      {options.map((o) => {
        const disabled = o.action === 'start' && blocked.length > 0;
        const button = (
          <Button
            key={o.action}
            data-action={o.action}
            danger={o.danger}
            disabled={disabled}
            onClick={() => open(o)}
          >
            {o.label}
          </Button>
        );
        return disabled ? (
          <Tooltip key={o.action} title={`Fill in first: ${blocked.join(', ')}`}>
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
      {blocked.length > 0 && options.some((o) => o.action === 'start') && (
        <Typography.Text type="warning" data-start-blocked>
          Start needs the pre-registration: {blocked.join(', ')}
        </Typography.Text>
      )}

      <Modal
        open={sizeFor !== null}
        title={sizeFor ? `${sizeFor.label} ${test.key}` : ''}
        okText={sizeFor?.label}
        okButtonProps={{ disabled: problem !== null }}
        onCancel={() => setSizeFor(null)}
        onOk={async () => {
          const o = sizeFor;
          const buckets = percent === null ? null : percentToBuckets(percent);
          if (!o || buckets === null) return;
          setSizeFor(null);
          await onRun(o, { rolloutSize: buckets });
        }}
      >
        <Typography.Paragraph>
          Live share of matching players, now {formatPercent(test.rolloutSize)} of the reserved{' '}
          {formatPercent(test.rangeSize)}.
        </Typography.Paragraph>
        <InputNumber
          id="expv2-action-percent"
          min={0.1}
          max={100}
          step={0.1}
          addonAfter="%"
          value={percent}
          onChange={(n) => setPercent(n)}
        />
        {growsRange && !problem && (
          <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
            Grows the reserved range into the free buckets after it; refused if they are taken.
          </Typography.Paragraph>
        )}
        {problem && (
          <Typography.Paragraph type="danger" style={{ marginTop: 8 }}>
            {problem}
          </Typography.Paragraph>
        )}
        {sizeFor?.requested && <Typography.Paragraph type="warning">{WAITS}</Typography.Paragraph>}
      </Modal>

      <Modal
        open={variantFor !== null}
        title={variantFor ? `${variantFor.label} ${test.key}` : ''}
        okText={variantFor?.label}
        okButtonProps={{ disabled: !variant, danger: variantFor?.danger }}
        onCancel={() => setVariantFor(null)}
        onOk={async () => {
          const o = variantFor;
          if (!o || !variant) return;
          setVariantFor(null);
          await onRun(o, { variant });
        }}
      >
        {variantFor && (
          <>
            <Typography.Paragraph>{VARIANT_CONSEQUENCE[variantFor.action]}</Typography.Paragraph>
            <Select
              id="expv2-action-variant"
              style={{ width: 220 }}
              placeholder="Variant"
              value={variant}
              options={variantChoices(test, variantFor.action).map((k) => ({ value: k, label: k }))}
              onChange={(v: string) => setVariant(v)}
            />
            {variantFor.requested && (
              <Typography.Paragraph type="warning" style={{ marginTop: 8 }}>
                {WAITS}
              </Typography.Paragraph>
            )}
          </>
        )}
      </Modal>
    </Space>
  );
};
